import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import { contractApi } from 'services/apis';
import { useMemo } from 'react';
import { ECurrencies } from 'constants/enums';

/**
 * Custom hook to determine if binder deployment is ready for peer review
 * This replicates the validation logic from BinderDeploymentTable and TotalDeploymentPerBinderTable
 */
export const useBinderDeploymentValidation = (
  submissionId: string | undefined,
  currency: string = ECurrencies.USD
) => {
  const { data: bindingData, isLoading: isLoadingBindingData } = useQuery(
    [EQueryKeys.BINDING_DATA_FOR_SUBMISSION_DETAILS, submissionId, currency],
    () =>
      contractApi.getBindingData({
        getBindingDataQueryDto: {
          submissionId: submissionId!,
          currency,
        },
      }),
    {
      enabled: !!submissionId,
    }
  );

  const {
    data: totalDeploymentPerBinder,
    isLoading: isLoadingTotalDeployment,
  } = useQuery(
    [EQueryKeys.TOTAL_DEPLOYMENT_PER_BINDER, submissionId],
    () =>
      contractApi.getTotalValuePerBinder({
        submissionId: submissionId!,
      }),
    {
      enabled: !!submissionId,
    }
  );

  const hasTotalDeploymentErrors = useMemo(() => {
    if (!totalDeploymentPerBinder) return true;

    return totalDeploymentPerBinder.some((item) => {
      return (
        item.excessOfLossAndQuotaShare.shareOfPremiumQuoted.isExceeded ||
        item.excessOfLossAndQuotaShare.shareOfLimitQuoted.isExceeded ||
        item.primary.shareOfLimitQuoted.isExceeded ||
        item.primary.shareOfPremiumQuoted.isExceeded
      );
    });
  }, [totalDeploymentPerBinder]);

  const hasBindingDataErrors = useMemo(() => {
    if (!bindingData) return true;

    const hasContracts = bindingData.some(
      (layer) => layer.contracts && layer.contracts.length > 0
    );

    return !hasContracts;
  }, [bindingData]);

  const isReadyForReview = useMemo(() => {
    if (isLoadingBindingData || isLoadingTotalDeployment) {
      return false;
    }

    if (hasTotalDeploymentErrors || hasBindingDataErrors) {
      return false;
    }

    return true;
  }, [
    isLoadingBindingData,
    isLoadingTotalDeployment,
    hasTotalDeploymentErrors,
    hasBindingDataErrors,
  ]);

  return {
    isReadyForReview,
  };
};
